import React, { useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Paper,
  IconButton,
  Button,
  TextField,
  Stack,
  Collapse,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ReplyIcon from '@mui/icons-material/Reply';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { formatDistanceToNow } from 'date-fns';
import { Comment } from '../../types/Comment';
import { useAuth } from '../../context/AuthContext';
import { SnackbarProvider, enqueueSnackbar } from 'notistack';
import { createComment, likeComment, dislikeComment, deleteComment } from '../../services/api';

interface CommentItemProps {
  comment: Comment;
  onReplyAdded: (reply: Comment, parentId: string) => void;
  onLikeDislike: (commentId: string, updated: Partial<Comment>) => void;
  onCommentDeleted: (commentId: string) => void;
  replies?: Comment[];
  depth?: number;
  parentUsername?: string;
}

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  onReplyAdded,
  onLikeDislike,
  onCommentDeleted,
  replies = [],
  depth = 0,
  parentUsername
}) => {
  const { user, isAuthenticated } = useAuth();
  const [openDialog, setOpenDialog] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [showAllReplies, setShowAllReplies] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [isDisliking, setIsDisliking] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedComment, setSelectedComment] = useState<Comment | any>(null);

  // Normalize comment data to ensure consistent structure
  // This handles cases where the backend returns populated userId fields (as objects)
  // vs newly created comments that have userId as strings
  const normalizedComment = {
    ...comment,
    userId: typeof comment.userId === 'object' ? comment.userId._id || comment.userId.id : comment.userId,
    id: comment._id || comment.id
  };

  // Show last 3 replies by default, show all when expanded
  const visibleReplies = showAllReplies ? replies : replies.slice(-1);
  const hasHiddenReplies = replies.length > 1;
  const hiddenRepliesCount = Math.max(0, replies.length - 1);
  const isShowingAllReplies = replies.length <= 1 || showAllReplies;

  const userLiked = user && normalizedComment.likedBy?.includes(user.id as string);
  const userDisliked = user && normalizedComment.dislikedBy?.includes(user.id as string);
  
  // Check if this is the current user's comment by comparing multiple possible field names
  const isOwnComment = user && (
    normalizedComment.userId === user.id || 
    normalizedComment.userId === user._id
  );

  // Debug logging to understand the data structure
  if (process.env.NODE_ENV === 'development') {
    console.log('Comment data structure:', {
      commentId: normalizedComment.id,
      commentUserId: normalizedComment.userId,
      commentUserIdType: typeof normalizedComment.userId,
      currentUserId: user?.id,
      currentUser_id: user?._id,
      isOwnComment
    });
  }

  const handleOpen = (comment?: Comment | any) => {
    setOpenDialog(true)
    setSelectedComment(comment)
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedComment(null);
  };

  const handleLike = async () => {
    if (!isAuthenticated || isLiking) return;
    setIsLiking(true);
    try {
      const { data } = await likeComment(normalizedComment.id);
      onLikeDislike(normalizedComment.id, data);
    } catch (err) {
        enqueueSnackbar((err as any).response.data.message, { variant: 'error' });
      // Optionally handle error
    } finally {
      setIsLiking(false);
    }
  };

  const handleDislike = async () => {
    if (!isAuthenticated || isDisliking) return;
    setIsDisliking(true);
    try {
      const { data } = await dislikeComment(normalizedComment.id);
      onLikeDislike(normalizedComment.id, data);
    } catch (err: any) {
      enqueueSnackbar(err.response.data.message, { variant: 'error' });
    } finally {
      setIsDisliking(false);
    }
  };

  const handleDelete = async () => {
    if (!isAuthenticated || isDeleting || !isOwnComment) return;
    
    setIsDeleting(true);
    try {
      await deleteComment(selectedComment.id || selectedComment._id);
      onCommentDeleted(selectedComment.id || selectedComment._id);
    } catch (err: any) {
      setError(err?.message || 'Failed to delete comment');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyContent.trim()) return;
    setIsReplying(true);
    setError(null);
    try {
      const dataToSend: any = {
        firstName: user?.firstName,
        lastName: user?.lastName,
        userId: user?.id,
        postId: normalizedComment.postId,
        content: replyContent,
        imageUrl: user?.imageUrl,
        parentId: normalizedComment.id
      };
      const { data } = await createComment(dataToSend, true); // true = isReply
      onReplyAdded(data, normalizedComment.id);
      setReplyContent('');
      setShowReplyForm(false);
      
      // Auto-expand replies to show the new reply
      if (replies.length >= 1) {
        setShowAllReplies(true);
      }
    } catch (err: any) {
      if(err?.response?.data?.errors) {
        err.response.data.errors.forEach((error: any) => {
          setError(error.msg || 'Failed to reply');
        });
      } else {
        setError(err.response.data.message || 'Failed to reply');
      }
    } finally {
      setIsReplying(false);
    }
  };

  // Compose the reply display
  const isReply = depth > 0 && parentUsername;
  const replyHeader = isReply ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
        <Typography variant="subtitle2" fontWeight="bold" color="primary.main" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
          {parentUsername}
        </Typography>
        <PlayArrowIcon fontSize="small" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }} />
        <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
          {normalizedComment.firstName} {normalizedComment.lastName}
        </Typography>
      </Box>
    </Box>
  ) : (
    <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
      {normalizedComment.firstName} {normalizedComment.lastName}
    </Typography>
  );

  return (
    <Box sx={{ ml: depth * 4, mt: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          bgcolor: 'background.paper',
          '&:hover': { bgcolor: 'background.default' }
        }}
      >
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Avatar
            src={normalizedComment.imageUrl}
            alt={`${normalizedComment.firstName} ${normalizedComment.lastName}`}
            sx={{ width: 40, height: 40, flexShrink: 0 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start',
              mb: 1,
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {replyHeader}
              </Box>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  flexShrink: 0,
                  whiteSpace: 'nowrap'
                }}
              >
                {formatDistanceToNow(new Date(normalizedComment.createdAt), { addSuffix: true })}
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              {normalizedComment.content}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              mt: 1,
              flexWrap: 'wrap',
              rowGap: 0.5
            }}>
              <IconButton size="small" onClick={handleLike} disabled={isLiking || !isAuthenticated} color={userLiked ? 'primary' : 'default'}>
                <ThumbUpIcon fontSize="small" />
                <Typography variant="caption" sx={{ ml: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                  {comment.likes}
                </Typography>
              </IconButton>
              <IconButton size="small" onClick={handleDislike} disabled={isDisliking || !isAuthenticated} color={userDisliked ? 'error' : 'default'}>
                <ThumbDownIcon fontSize="small" />
                <Typography variant="caption" sx={{ ml: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                  {comment.dislikes}
                </Typography>
              </IconButton>
              <Button
                size="small"
                startIcon={<ReplyIcon />}
                onClick={() => setShowReplyForm((v) => !v)}
                disabled={!isAuthenticated}
                sx={{ 
                  textTransform: 'none',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  minWidth: 'auto',
                  px: { xs: 1, sm: 1.5 }
                }}
              >
                Reply
              </Button>
              {isOwnComment && (
                <IconButton
                  size="small"
                  onClick={() => handleOpen(comment)}
                  disabled={isDeleting}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
              {hasHiddenReplies && (
                <Button
                  size="small"
                  onClick={() => setShowAllReplies((v) => !v)}
                  sx={{ 
                    textTransform: 'none', 
                    ml: 'auto',
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    minWidth: 'auto',
                    px: { xs: 1, sm: 1.5 },
                    whiteSpace: 'nowrap'
                  }}
                >
                  {showAllReplies ? 'Hide Previous Replies' : `Show Previous Replies (${hiddenRepliesCount})`}
                </Button>
              )}
              
              {/* Show total reply count when there are 3 or fewer replies */}
              {!hasHiddenReplies && replies.length > 0 && (
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    ml: 'auto',
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    whiteSpace: 'nowrap'
                  }}
                >
                  {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                </Typography>
              )}
            </Box>
            {showReplyForm && (
              <Box component="form" onSubmit={handleReply} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={`Reply to ${comment.firstName}`}
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  multiline
                  rows={2}
                  required
                />
                {error && (
                  <Alert severity="error" sx={{ width: '100%', my: 1 }}>{error}</Alert>
                )}
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button type="submit" variant="contained" size="small" disabled={isReplying}>
                    {isReplying ? <CircularProgress size={18} /> : 'Post Reply'}
                  </Button>
                  <Button size="small" onClick={() => setShowReplyForm(false)}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
      {/* Replies (recursive) */}
      {/* Always show last 3 replies by default */}
      {visibleReplies.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {visibleReplies.map((reply) => (
            <CommentItem
              key={reply.id || reply._id}
              comment={reply}
              onReplyAdded={onReplyAdded}
              onLikeDislike={onLikeDislike}
              onCommentDeleted={onCommentDeleted}
              replies={reply.replies || []}
              depth={depth + 1}
              parentUsername={`${normalizedComment.firstName} ${normalizedComment.lastName}`}
            />
          ))}
          
          {/* Show indicator when there are hidden replies */}
          {hasHiddenReplies && !showAllReplies && (
            <Box sx={{ 
              textAlign: 'center', 
              py: 1, 
              px: 2,
              color: 'text.secondary',
              fontSize: '0.75rem',
              borderTop: '1px solid',
              borderColor: 'divider',
              mt: 1
            }}>
              {hiddenRepliesCount} previous {hiddenRepliesCount === 1 ? 'reply' : 'replies'} hidden • Click "Show Previous Replies" to view all
            </Box>
          )}
        </Stack>
      )}
      
      {/* Show hidden replies when expanded */}
      <Collapse in={showAllReplies} timeout="auto" unmountOnExit>
        {hasHiddenReplies && (
          <Stack spacing={1} sx={{ mt: 1 }}>
            {replies.slice(0, -1).map((reply) => (
              <CommentItem
                key={reply.id || reply._id}
                comment={reply}
                onReplyAdded={onReplyAdded}
                onLikeDislike={onLikeDislike}
                onCommentDeleted={onCommentDeleted}
                replies={reply.replies || []}
                depth={depth + 1}
                parentUsername={`${normalizedComment.firstName} ${normalizedComment.lastName}`}
              />
            ))}
          </Stack>
        )}
      </Collapse>
      <Dialog
        open={openDialog}
        onClose={handleClose}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this comment "{selectedComment?.content}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleDelete} color="error" autoFocus disabled={isDeleting}>
            {isDeleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <SnackbarProvider />
    </Box>
  );
};

export default CommentItem; 